import * as THREE from 'three';

/**
 * Animated aurora borealis: two large curved curtain planes with a noise
 * shader, additive-blended against the night sky.
 */
export interface Aurora {
  group: THREE.Group;
  update(elapsed: number): void;
  /** 0..1 current brightness — locations use it to tint their lighting. */
  glow(elapsed: number): number;
}

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform float uTime;
  uniform float uPhase;
  varying vec2 vUv;

  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x),
               mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
  }
  float fbm(vec2 p) {
    return noise(p) * 0.55 + noise(p * 2.1) * 0.28 + noise(p * 4.3) * 0.17;
  }

  void main() {
    float t = uTime * 0.06 + uPhase;
    // Curtain center line wanders horizontally over time.
    float center = 0.45 + (fbm(vec2(vUv.x * 2.4 + t, t * 0.7)) - 0.5) * 0.55;
    float dist = abs(vUv.y - center);
    float curtain = exp(-dist * dist * 26.0);
    // Vertical rays.
    float rays = 0.65 + 0.35 * noise(vec2(vUv.x * 42.0, t * 2.0));
    // Slow global breathing.
    float breathe = 0.72 + 0.28 * sin(uTime * 0.21 + uPhase * 3.0);
    float i = curtain * rays * breathe;

    vec3 green = vec3(0.15, 0.95, 0.55);
    vec3 teal = vec3(0.1, 0.7, 0.8);
    vec3 purple = vec3(0.55, 0.25, 0.85);
    vec3 col = mix(green, teal, noise(vec2(vUv.x * 3.0 + t, 7.0)));
    col = mix(col, purple, smoothstep(0.0, 0.45, vUv.y - center));

    // Fade the plane's outer edges.
    float edge = smoothstep(0.0, 0.12, vUv.x) * smoothstep(1.0, 0.88, vUv.x)
               * smoothstep(0.0, 0.1, vUv.y) * smoothstep(1.0, 0.85, vUv.y);
    gl_FragColor = vec4(col * i, i * 0.55) * edge;
  }
`;

export function createAurora(): Aurora {
  const group = new THREE.Group();
  const materials: THREE.ShaderMaterial[] = [];

  const makeCurtain = (phase: number, y: number, z: number, ry: number, w: number, h: number) => {
    const mat = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: { uTime: { value: 0 }, uPhase: { value: phase } },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
    });
    materials.push(mat);
    const geo = new THREE.PlaneGeometry(w, h, 32, 1);
    // Gentle horizontal curve so the curtain wraps the sky.
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      pos.setZ(i, -Math.pow(x / (w / 2), 2) * 40);
    }
    pos.needsUpdate = true;
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(0, y, z);
    mesh.rotation.y = ry;
    mesh.frustumCulled = false;
    group.add(mesh);
  };

  makeCurtain(0, 150, -220, 0.1, 620, 150);
  makeCurtain(2.3, 175, -150, -0.35, 520, 120);

  return {
    group,
    update(elapsed: number) {
      for (const m of materials) m.uniforms.uTime!.value = elapsed;
    },
    glow(elapsed: number) {
      return 0.72 + 0.28 * Math.sin(elapsed * 0.21);
    },
  };
}
